import { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { createRoot, type Root } from "react-dom/client";
import {
  Box,
  FormControl,
  FormLabel,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  Checkbox,
  Paper,
  ThemeProvider,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import type { TradePriceTileFilter } from "../types/tradePrice";
import { theme } from "@/types/theme";

interface Props {
  filter: TradePriceTileFilter;
  onFilterChange: (filter: TradePriceTileFilter) => void;
}

/**
 * 取引価格タイルフィルタコントロール（Leafletのカスタムコントロール）
 */
export const TradePriceTileFilterControl: React.FC<Props> = ({
  filter,
  onFilterChange,
}) => {
  const map = useMap();
  const controlRef = useRef<L.Control | null>(null);
  const rootRef = useRef<Root | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!map) return;

    // カスタムコントロールを作成
    const FilterControl = L.Control.extend({
      onAdd: function () {
        const container = L.DomUtil.create("div", "leaflet-bar");
        container.style.background = "none";
        container.style.border = "none";

        // クリックイベントの伝播を防止（地図のクリックイベントと干渉しないように）
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    const control = new FilterControl({ position: "topright" });
    control.addTo(map);
    controlRef.current = control;

    // Reactコンポーネントをマウント
    const container = control.getContainer();
    if (container) {
      const root = createRoot(container);
      rootRef.current = root;
      root.render(
        <ThemeProvider theme={theme}>
          <FilterPanelContent
            filter={filter}
            onFilterChange={onFilterChange}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        </ThemeProvider>
      );
    }

    return () => {
      const cleanupRoot = rootRef.current;
      if (cleanupRoot) {
        setTimeout(() => {
          cleanupRoot.unmount();
        }, 0);
      }
      if (controlRef.current && map) {
        map.removeControl(controlRef.current);
      }
    };
  }, [map]);

  // フィルタまたは開閉状態が変更されたら再レンダリング
  useEffect(() => {
    if (rootRef.current) {
      rootRef.current.render(
        <ThemeProvider theme={theme}>
          <FilterPanelContent
            filter={filter}
            onFilterChange={onFilterChange}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        </ThemeProvider>
      );
    }
  }, [filter, onFilterChange, isOpen]);

  return null;
};

/**
 * フィルタパネルの内容
 */
interface FilterPanelContentProps extends Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const FilterPanelContent: React.FC<FilterPanelContentProps> = ({
  filter,
  onFilterChange,
  isOpen,
  setIsOpen,
}) => {
  const handlePriceClassificationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target
      .value as (typeof filter.priceClassifications)[number];
    const isChecked = event.target.checked;

    const newPriceClassifications = isChecked
      ? [...filter.priceClassifications, value]
      : filter.priceClassifications.filter((type) => type !== value);

    onFilterChange({
      ...filter,
      priceClassifications: newPriceClassifications,
    });
  };

  const handlePeriodChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = Number(event.target.value);
    if (value === 1 || value === 2 || value === 5) {
      onFilterChange({
        ...filter,
        periodYears: value,
      });
    }
  };

  const handlePropertyTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value as (typeof filter.propertyTypes)[number];
    const isChecked = event.target.checked;

    const newPropertyTypes = isChecked
      ? [...filter.propertyTypes, value]
      : filter.propertyTypes.filter((type) => type !== value);

    onFilterChange({
      ...filter,
      propertyTypes: newPropertyTypes,
    });
  };

  return (
    <div
      onMouseEnter={() => {
        setIsOpen(true);
      }}
      onMouseLeave={() => {
        setIsOpen(false);
      }}
      onClick={() => {
        setIsOpen(true);
      }}
      style={{
        position: "relative",
      }}
    >
      {/* 閉じた状態: フィルターアイコンのみ表示 */}
      {!isOpen && (
        <div
          style={{
            width: "42px",
            height: "42px",
            background: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
          }}
        >
          <FilterListIcon fontSize="medium" htmlColor="#4b5563" />
        </div>
      )}

      {/* 開いた状態: フィルタパネル全体を表示 */}
      {isOpen && (
        <Paper
          elevation={3}
          sx={{
            padding: 2,
            minWidth: 240,
            maxWidth: 260,
          }}
        >
          <Box>
            <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
              <FormLabel component="legend" sx={{ fontSize: "0.9rem", mb: 1 }}>
                価格情報区分
              </FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={filter.priceClassifications.includes(
                        "realEstateTransaction"
                      )}
                      onChange={handlePriceClassificationChange}
                      value="realEstateTransaction"
                    />
                  }
                  label="不動産取引価格情報"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={filter.priceClassifications.includes(
                        "contractPrice"
                      )}
                      onChange={handlePriceClassificationChange}
                      value="contractPrice"
                    />
                  }
                  label="成約価格情報"
                />
              </FormGroup>
            </FormControl>

            <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
              <FormLabel component="legend" sx={{ fontSize: "0.9rem", mb: 1 }}>
                取引時期
              </FormLabel>
              <RadioGroup
                value={filter.periodYears}
                onChange={handlePeriodChange}
              >
                <FormControlLabel
                  value={1}
                  control={<Radio size="small" color="primary" />}
                  label="直近1年"
                />
                <FormControlLabel
                  value={2}
                  control={<Radio size="small" color="primary" />}
                  label="直近2年"
                />
                <FormControlLabel
                  value={5}
                  control={<Radio size="small" color="primary" />}
                  label="直近5年"
                />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ fontSize: "0.9rem", mb: 1 }}>
                物件種別
              </FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={filter.propertyTypes.includes("land")}
                      onChange={handlePropertyTypeChange}
                      value="land"
                    />
                  }
                  label="土地"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={filter.propertyTypes.includes(
                        "landWithBuilding"
                      )}
                      onChange={handlePropertyTypeChange}
                      value="landWithBuilding"
                    />
                  }
                  label="土地付建物"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={filter.propertyTypes.includes("usedCondominium")}
                      onChange={handlePropertyTypeChange}
                      value="usedCondominium"
                    />
                  }
                  label="中古マンション"
                />
              </FormGroup>
            </FormControl>
          </Box>
        </Paper>
      )}
    </div>
  );
};
